import "../custom.css";

import { Button, Container, Nav, Tab } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSync, faPhone } from "@fortawesome/free-solid-svg-icons";
import { useContext, useEffect, useState } from "react";

import { DocumentsTab } from "./DocumentsTab";
import { MainContext } from "../App";
import { RoutesTab } from "./RoutesTab";
import apiService from "../api";

export const formatNumberAsCurrency = (value) => {
  return new Intl.NumberFormat("tr-TR", {
    style: "currency",
    currency: "TRY",
    minimumFractionDigits: 2,
  }).format(value);
};

export const CustomerDetailPage = (props) => {
  const { accessToken, setSasToken, setProgress, setProjectLogo, setProjectName: setGlobalProjectName } = useContext(MainContext);
  const [customerSummary, setCustomerSummary] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [projectName, setProjectName] = useState("");
  const [projectId, setProjectId] = useState(null);
  const [projectManager, setProjectManager] = useState(null);
  const [activeTab, setActiveTab] = useState("routes");
  const [refreshKey, setRefreshKey] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const fetchCustomerSummary = () => {
    return apiService
      .getWithFirstResolve("customersummary", accessToken, setProgress)
      .then((data) => {
        setSasToken(data.headers.get("Sas-Token"));
        localStorage.setItem("sasToken", data.headers.get("Sas-Token"));
        return data.json();
      })
      .then((data) => {
        setCustomerSummary(data);
        // Assume single project - use first one
        const project = data.projects?.[0];
        if (project) {
          setProjectId(project.id);
          setProjectName(project.name);
          setGlobalProjectName(project.name);
          setProjectLogo(project.logoUrl);
          setProjectManager(project.projectManager);
          setVehicles(
            project.routes
              ?.sort((a, b) => a?.name?.localeCompare(b?.name))
              ?.map((route) => ({
                ...route,
                ...data.vehicles[route?.vehicleId],
              }))
          );
        }
      });
  };

  useEffect(() => {
    fetchCustomerSummary();
  }, [accessToken, setSasToken, setProgress, setProjectLogo, setGlobalProjectName]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      if (activeTab === 'routes') {
        await fetchCustomerSummary();
      } else if (activeTab === 'documents') {
        setRefreshKey(prev => prev + 1);
      }
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleVehicleClick = (vehicle) => {
    localStorage.setItem('customerSummary', JSON.stringify(customerSummary));
    window.open(`/vehicleDetail/${vehicle.vehicleId}/?routeId=${vehicle._id}`, '_blank');
  };

  return (
    <Container fluid style={{ paddingTop: "15px", minHeight: "100vh" }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '15px',
        flexWrap: 'wrap',
        gap: '10px'
      }}>
        <h2 style={{
          color: "#5a9bd4",
          margin: 0,
          textShadow: "0 0 10px rgba(20, 52, 92, 0.5)",
          fontWeight: "700"
        }}>
          {projectName}
        </h2>
        {projectManager && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '10px 16px',
            background: 'rgba(20, 52, 92, 0.15)',
            borderRadius: '10px',
            border: '1px solid rgba(20, 52, 92, 0.3)'
          }}>
            <div style={{
              fontSize: '0.75rem',
              color: '#888',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              marginRight: '5px'
            }}>
              Proje Sorumlusu
            </div>
            <div style={{
              width: '1px',
              height: '24px',
              background: 'rgba(90, 155, 212, 0.3)'
            }} />
            <span style={{ color: '#5a9bd4', fontWeight: '600', fontSize: '0.95rem' }}>
              {projectManager.name}
            </span>
            {projectManager.phone && (
              <a
                href={`tel:${projectManager.phone.replace(/\s/g, '')}`}
                style={{
                  color: '#fff',
                  textDecoration: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '5px 10px',
                  background: 'rgba(90, 155, 212, 0.3)',
                  borderRadius: '6px',
                  fontSize: '0.85rem',
                  transition: 'background 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(90, 155, 212, 0.5)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(90, 155, 212, 0.3)'}
              >
                <FontAwesomeIcon icon={faPhone} size="sm" />
                {projectManager.phone}
              </a>
            )}
          </div>
        )}
      </div>
      <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Nav variant="tabs" className="dashboard-tabs">
            <Nav.Item>
              <Nav.Link eventKey="routes">Güzergah Listesi</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="documents">Evrak Listesi</Nav.Link>
            </Nav.Item>
          </Nav>
          <Button
            size="sm"
            onClick={handleRefresh}
            disabled={isRefreshing}
            style={{
              background: "rgba(20, 52, 92, 0.2)",
              border: "1px solid rgba(20, 52, 92, 0.5)",
              color: "#5a9bd4",
              marginBottom: '5px'
            }}
          >
            <FontAwesomeIcon
              icon={faSync}
              spin={isRefreshing}
              style={{ marginRight: '5px' }}
            />
            Yenile
          </Button>
        </div>
        <Tab.Content className="dashboard-tab-content">
          <Tab.Pane eventKey="routes">
            <RoutesTab
              vehicles={vehicles}
              projectName={projectName}
              onVehicleClick={handleVehicleClick}
            />
          </Tab.Pane>
          <Tab.Pane eventKey="documents">
            <DocumentsTab
              vehicles={vehicles}
              projectName={projectName}
              projectId={projectId}
              isActive={activeTab === 'documents'}
              refreshKey={refreshKey}
            />
          </Tab.Pane>
        </Tab.Content>
      </Tab.Container>
    </Container>
  );
};
